/**
 * GEO 改造的常量单一来源。
 *
 * 这里的每一张表都是「多个消费方共用一份数据」：
 * - `AI_CRAWLERS` / `LEGACY_AGENTS`：`src/seo/robots.ts` 生成 robots.txt 声明段、
 *   nginx `ai-crawlers.conf` 的 `map $http_user_agent` 片段、
 *   `scripts/geo-inspect/uaMatrix.ts` 的 UA 矩阵（需求 9.2、9.6、9.7、12.2）
 * - `PAGE_TYPE_META_PRESET`：元数据兜底文案与内容质量下限（需求 2、4.16、10）
 * - `CHANGEFREQ_PRIORITY`：sitemap 的 `<changefreq>` / `<priority>`（需求 8.14）
 *
 * 三张 Page_Type 相关的表全部声明为 `Record<PageType, ...>`，
 * 「新增 Page_Type 忘了配」在编译期即报错。
 *
 * 对应需求：8.14、9.2、10.6、10.13、10.14、12.2
 */

import type { ChangeFreq, ContentBounds, PageType, PageTypePreset, SitemapFrequency } from "./types";

/** 生产环境的站点规范域名（WWW_Domain）；未配置 `VITE_SITE_ORIGIN` 时使用 */
export const DEFAULT_SITE_ORIGIN = "https://www.ogolden.com";

/**
 * 解析站点规范域名：把环境变量 `VITE_SITE_ORIGIN` 归一化为「协议 + 主机[:端口]」，
 * 不带末尾斜杠、路径、查询串。未配置、为空或格式不合法（不是 http/https 绝对地址，
 * 或带路径/查询/账号信息）时回退到 {@link DEFAULT_SITE_ORIGIN}。
 *
 * 用途：测试环境等非生产域名部署同一份代码时，让 canonical / og:url / sitemap /
 * JSON-LD 的绝对地址指向该环境自己的域名；生产环境不配置此变量，行为与此前完全一致。
 *
 * 纯函数，导出仅为便于测试。
 */
export function resolveSiteOrigin(raw: unknown): string {
  if (typeof raw !== "string") return DEFAULT_SITE_ORIGIN;
  const trimmed = raw.trim().replace(/\/+$/, "");
  if (trimmed === "") return DEFAULT_SITE_ORIGIN;
  try {
    const url = new URL(trimmed);
    const originOnly =
      (url.protocol === "https:" || url.protocol === "http:") &&
      url.username === "" &&
      url.password === "" &&
      (url.pathname === "/" || url.pathname === "") &&
      url.search === "" &&
      url.hash === "";
    return originOnly ? url.origin : DEFAULT_SITE_ORIGIN;
  } catch {
    return DEFAULT_SITE_ORIGIN;
  }
}

/**
 * 读取 `VITE_SITE_ORIGIN`。本文件同时被两类运行环境引用：
 * - Vite 打包的浏览器代码 / vitest：通过 `import.meta.env` 读取（构建期静态替换）
 * - `tsx` 直接执行的 Node 脚本（`scripts/prerender`、`scripts/geo-inspect`）：
 *   没有 `import.meta.env`（访问会抛 TypeError），改读 `process.env`
 * 两处都读不到时返回 `undefined`，由 {@link resolveSiteOrigin} 回退默认值。
 */
function readConfiguredSiteOrigin(): string | undefined {
  try {
    const fromVite = import.meta.env.VITE_SITE_ORIGIN;
    if (typeof fromVite === "string" && fromVite.trim() !== "") return fromVite;
  } catch {
    // tsx 环境下 import.meta.env 为 undefined，继续尝试 process.env
  }
  try {
    const fromProcess = (globalThis as { process?: { env?: Record<string, string | undefined> } }).process?.env
      ?.VITE_SITE_ORIGIN;
    if (typeof fromProcess === "string" && fromProcess.trim() !== "") return fromProcess;
  } catch {
    // 浏览器中没有 process，忽略
  }
  return undefined;
}

/** 站点唯一对外规范域名（WWW_Domain），全站绝对地址前缀；测试环境可经 `VITE_SITE_ORIGIN` 覆盖 */
export const SITE_ORIGIN = resolveSiteOrigin(readConfiguredSiteOrigin());

/** 兜底社交分享图，绝对地址，对应 `public/favicon.png`（需求 2.4、4.16） */
export const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/favicon.png`;

/** sitemap 地址，robots.txt 的 `Sitemap:` 行与 sitemap index 共用（需求 9.5） */
export const SITEMAP_URL = `${SITE_ORIGIN}/sitemap.xml`;

/**
 * AI_Crawler 的 17 个 User-Agent 标识，按需求 Glossary 的定义顺序、
 * 大小写逐字符一致。
 *
 * `Bytespider` 同时出现在本表与现有 robots.txt 的既有声明段中，
 * 生成器按「已在 `AI_CRAWLERS` 中则不重复输出」去重（见 `src/seo/robots.ts`）。
 *
 * 需求 9.2、12.2
 */
export const AI_CRAWLERS = [
  "GPTBot",
  "ChatGPT-User",
  "OAI-SearchBot",
  "Claude-Web",
  "ClaudeBot",
  "anthropic-ai",
  "PerplexityBot",
  "Perplexity-User",
  "Google-Extended",
  "Applebot",
  "Applebot-Extended",
  "CCBot",
  "Bytespider",
  "Amazonbot",
  "meta-externalagent",
  "DeepSeekBot",
  "YisouSpider",
] as const;

/**
 * 现有 robots.txt 中已显式声明的 6 个 User-agent 段，改造后需保留。
 * `Disallow: /api/` 仅出现在 `*` 段（需求 9.3、9.4、9.11）。
 */
export const LEGACY_AGENTS = [
  "*",
  "Baiduspider",
  "Googlebot",
  "bingbot",
  "Sogou web spider",
  "360Spider",
] as const;

/**
 * 12 个 Page_Type 的兜底文案与内容质量下限。
 *
 * `/contact` 的三项放宽值（h2Min=2、imgMin=1、visibleTextMin=400）以数据形式
 * 存在于 `CONTACT` 条目里，调用方一律读表、不做 Page_Type 分支判断；
 * 待确认事项 16 回收放宽时只改这一条数据（需求 10.6、10.13、10.14）。
 *
 * `internalLinkMin` 全部为 20：需求 10.9 对内部链接数量没有 Page_Type 差异，
 * 但仍逐条写出，避免调用方在读不到值时回落到隐式默认。
 */
export const PAGE_TYPE_META_PRESET: Record<PageType, PageTypePreset> = {
  HOME: {
    titleTemplate: "{name} - 企业级网络安全与IT基础架构解决方案",
    fallbackTitle: "欧格登智能科技 - 企业级网络安全与IT基础架构解决方案服务商",
    descriptionTemplate:
      "{name}提供信息安全、IT基础架构、云信达、AI业务四大领域的企业级解决方案与产品服务，覆盖等保合规、零信任、数据中心、云计算与容灾备份，为全国政企客户提供咨询、实施与运维一体化支持。",
    keywordSeeds: [
      "欧格登",
      "欧格登智能科技",
      "信息安全",
      "IT基础架构",
      "云信达",
      "AI业务",
      "企业安全解决方案",
      "等保三级",
    ],
    fallbackOgImage: DEFAULT_OG_IMAGE,
    breadcrumbLabel: "首页",
    ogType: "website",
    bounds: { h2Min: 3, imgMin: 3, visibleTextMin: 1200, internalLinkMin: 20 },
    renderFaq: true,
  },

  SOLUTION_LIST: {
    titleTemplate: "{name} - 欧格登企业级解决方案",
    fallbackTitle: "解决方案中心 - 欧格登智能科技企业级信息化解决方案",
    descriptionTemplate:
      "{name}覆盖信息安全、IT基础架构、云信达与AI业务四大领域，欧格登智能科技为政企客户提供从规划咨询到落地实施与持续运维的一体化解决方案与技术支持。",
    keywordSeeds: [
      "解决方案",
      "信息安全解决方案",
      "IT基础架构",
      "云信达",
      "AI业务",
      "等保合规",
      "零信任",
      "企业信息化",
    ],
    fallbackOgImage: DEFAULT_OG_IMAGE,
    breadcrumbLabel: "解决方案",
    ogType: "website",
    bounds: { h2Min: 3, imgMin: 3, visibleTextMin: 600, internalLinkMin: 20 },
    renderFaq: false,
  },

  SOLUTION_CATEGORY: {
    titleTemplate: "{name}解决方案 - 欧格登智能科技",
    fallbackTitle: "解决方案分类 - 欧格登智能科技分领域解决方案汇总",
    descriptionTemplate:
      "{name}分类下汇集欧格登智能科技的相关解决方案、典型架构与落地案例，涵盖需求分析、方案设计、产品选型、部署实施与运维保障各环节，帮助企业快速匹配合适方案。",
    keywordSeeds: [
      "解决方案分类",
      "信息安全",
      "IT基础架构",
      "云信达",
      "AI业务",
      "行业解决方案",
      "技术架构",
    ],
    fallbackOgImage: DEFAULT_OG_IMAGE,
    breadcrumbLabel: "解决方案分类",
    ogType: "website",
    bounds: { h2Min: 3, imgMin: 3, visibleTextMin: 600, internalLinkMin: 20 },
    renderFaq: true,
  },

  SOLUTION_DETAIL: {
    titleTemplate: "{name} - 欧格登解决方案",
    fallbackTitle: "解决方案详情 - 欧格登智能科技行业解决方案介绍",
    descriptionTemplate:
      "{name}由欧格登智能科技规划与交付，内容包含方案背景、总体架构、核心能力、关键产品与实施要点，并提供同类场景的落地经验与运维支持方式，便于快速评估适配度。",
    keywordSeeds: [
      "解决方案",
      "方案架构",
      "技术选型",
      "落地实施",
      "运维服务",
      "欧格登智能科技",
    ],
    fallbackOgImage: DEFAULT_OG_IMAGE,
    breadcrumbLabel: "方案详情",
    ogType: "article",
    bounds: { h2Min: 3, imgMin: 3, visibleTextMin: 800, internalLinkMin: 20 },
    renderFaq: true,
  },

  PRODUCT_LIST: {
    titleTemplate: "{name} - 欧格登产品中心",
    fallbackTitle: "产品中心 - 欧格登智能科技企业级安全与IT产品",
    descriptionTemplate:
      "{name}汇集欧格登智能科技代理与集成的企业级安全、网络、服务器、存储与云平台产品，提供选型建议、兼容性说明、部署方式与售后服务信息，支持按需组合成套方案。",
    keywordSeeds: [
      "产品中心",
      "安全产品",
      "网络设备",
      "服务器",
      "存储备份",
      "云平台",
      "欧格登产品",
    ],
    fallbackOgImage: DEFAULT_OG_IMAGE,
    breadcrumbLabel: "产品中心",
    ogType: "website",
    bounds: { h2Min: 3, imgMin: 3, visibleTextMin: 600, internalLinkMin: 20 },
    renderFaq: false,
  },

  PRODUCT_DETAIL: {
    titleTemplate: "{name} - 欧格登产品",
    fallbackTitle: "产品详情 - 欧格登智能科技企业级产品参数与选型",
    descriptionTemplate:
      "{name}的产品定位、核心功能、技术参数、适用场景与部署方式说明，由欧格登智能科技提供选型建议、兼容性评估、上线实施与售后运维服务，可结合整体方案一并交付。",
    keywordSeeds: [
      "产品参数",
      "技术规格",
      "适用场景",
      "部署方式",
      "选型建议",
      "欧格登产品",
    ],
    fallbackOgImage: DEFAULT_OG_IMAGE,
    breadcrumbLabel: "产品详情",
    ogType: "product",
    bounds: { h2Min: 3, imgMin: 3, visibleTextMin: 800, internalLinkMin: 20 },
    renderFaq: true,
  },

  CASE_LIST: {
    titleTemplate: "{name} - 欧格登成功案例",
    fallbackTitle: "成功案例 - 欧格登智能科技政企客户落地实践",
    descriptionTemplate:
      "{name}收录欧格登智能科技在政府、金融、制造、医疗、教育等行业的信息安全与IT基础架构落地实践，涵盖客户背景、需求痛点、方案构成与实施成效等完整信息。",
    keywordSeeds: [
      "成功案例",
      "客户案例",
      "信息安全案例",
      "IT基础架构案例",
      "行业实践",
      "项目实施",
    ],
    fallbackOgImage: DEFAULT_OG_IMAGE,
    breadcrumbLabel: "成功案例",
    ogType: "website",
    bounds: { h2Min: 3, imgMin: 3, visibleTextMin: 600, internalLinkMin: 20 },
    renderFaq: false,
  },

  CASE_DETAIL: {
    titleTemplate: "{name} - 欧格登客户案例",
    fallbackTitle: "案例详情 - 欧格登智能科技客户项目实施纪实",
    descriptionTemplate:
      "{name}记录客户背景、业务痛点、方案构成、实施过程与最终成效，由欧格登智能科技交付并持续运维，可作为同行业同类需求的参考实践与选型依据，也可用于内部评审与预算测算。",
    keywordSeeds: ["客户案例", "项目实施", "方案落地", "实施成效", "行业实践"],
    fallbackOgImage: DEFAULT_OG_IMAGE,
    breadcrumbLabel: "案例详情",
    ogType: "article",
    bounds: { h2Min: 3, imgMin: 3, visibleTextMin: 800, internalLinkMin: 20 },
    renderFaq: false,
  },

  NEWS_LIST: {
    titleTemplate: "{name} - 欧格登行业资讯",
    fallbackTitle: "行业资讯 - 欧格登智能科技安全与信息化动态",
    descriptionTemplate:
      "{name}发布信息安全、IT基础架构、云计算与AI应用领域的政策解读、技术趋势、产品更新与公司动态，由欧格登智能科技整理，便于持续跟进行业变化与合规要求。",
    keywordSeeds: [
      "行业资讯",
      "安全动态",
      "政策解读",
      "技术趋势",
      "公司新闻",
      "信息化动态",
    ],
    fallbackOgImage: DEFAULT_OG_IMAGE,
    breadcrumbLabel: "行业资讯",
    ogType: "website",
    bounds: { h2Min: 3, imgMin: 3, visibleTextMin: 600, internalLinkMin: 20 },
    renderFaq: false,
  },

  NEWS_DETAIL: {
    titleTemplate: "{name} - 欧格登资讯",
    fallbackTitle: "资讯详情 - 欧格登智能科技行业动态与政策解读",
    descriptionTemplate:
      "{name}由欧格登智能科技整理发布，内容涉及行业政策、技术趋势与实践经验，可作为企业安全建设与信息化规划的参考资料，也欢迎结合自身业务场景与我们进一步沟通交流。",
    keywordSeeds: ["行业资讯", "安全动态", "政策解读", "技术趋势", "实践经验"],
    fallbackOgImage: DEFAULT_OG_IMAGE,
    breadcrumbLabel: "资讯详情",
    ogType: "article",
    bounds: { h2Min: 3, imgMin: 3, visibleTextMin: 800, internalLinkMin: 20 },
    renderFaq: false,
  },

  ABOUT: {
    titleTemplate: "{name} - 关于欧格登智能科技",
    fallbackTitle: "关于欧格登智能科技 - 公司简介与资质荣誉",
    descriptionTemplate:
      "{name}介绍欧格登智能科技的公司概况、发展历程、资质认证、技术团队与合作伙伴，以及在信息安全与IT基础架构领域的服务能力、交付经验与联系方式，帮助客户全面了解我们。",
    keywordSeeds: [
      "关于欧格登",
      "公司简介",
      "资质认证",
      "发展历程",
      "合作伙伴",
      "技术团队",
    ],
    fallbackOgImage: DEFAULT_OG_IMAGE,
    breadcrumbLabel: "关于我们",
    ogType: "website",
    bounds: { h2Min: 3, imgMin: 3, visibleTextMin: 1200, internalLinkMin: 20 },
    renderFaq: true,
  },

  CONTACT: {
    titleTemplate: "{name} - 联系欧格登智能科技",
    fallbackTitle: "联系我们 - 欧格登智能科技电话邮箱与办公地址",
    descriptionTemplate:
      "{name}提供欧格登智能科技的联系电话、电子邮箱、办公地址与在线留言入口，方案咨询、产品选型、项目实施与售后运维需求均可直接联系我们，工作日内快速响应。",
    keywordSeeds: [
      "联系我们",
      "联系电话",
      "电子邮箱",
      "办公地址",
      "在线留言",
      "售前咨询",
    ],
    fallbackOgImage: DEFAULT_OG_IMAGE,
    breadcrumbLabel: "联系我们",
    ogType: "website",
    // 需求 10.13、10.14、10.6 对 /contact 的放宽值：以数据形式存在，
    // 调用方不得改为 `if (pageType === 'CONTACT')` 分支
    bounds: { h2Min: 2, imgMin: 1, visibleTextMin: 400, internalLinkMin: 20 },
    renderFaq: true,
  },
};

/**
 * 按规范化 URL 键（`pathname` + 可选 `?category=x`，见 `src/seo/bounds.ts` 的
 * `boundsKeyOf()`）放宽个别页面的内容下限；键不在表内则完全使用 Page_Type 预设。
 *
 * 为什么不改 `PAGE_TYPE_META_PRESET`：它按 Page_Type 整体生效，改
 * `SOLUTION_CATEGORY` 会连带放宽另外三个分类页。这里只对单个 URL 放宽，
 * 且只写需要放宽的字段（`Partial`），其余字段仍取 Page_Type 预设。
 *
 * 调用方一律经 `resolveBounds(pageType, url)` 读取，不得直接读本表。
 *
 * 对应 ai-business-overview-page 需求 9.4、9.5、设计决策 D8。
 */
export const URL_BOUNDS_OVERRIDE: Readonly<Record<string, Partial<ContentBounds>>> = {
  // /solutions?category=ai-business：主体来自设计稿、不含图片；站点 chrome（Navbar / Footer）
  // 最多提供 2 个 logo，达不到 SOLUTION_CATEGORY 的 imgMin=3。logo 是否存在取决于后台
  // `companyInfo.logo` 配置，取 0（而非 2）使自检结果与后台配置无关。
  // h2Min、visibleTextMin、internalLinkMin 不放宽。
  "/solutions?category=ai-business": { imgMin: 0 },
};

/**
 * sitemap 的 `<changefreq>` 与 `<priority>`，逐行对应需求 8.14。
 * `priority` 为字符串，避免 `1.0` 被序列化成 `1`。
 */
export const CHANGEFREQ_PRIORITY: Record<PageType, SitemapFrequency> = {
  HOME: { changefreq: "daily", priority: "1.0" },
  NEWS_LIST: { changefreq: "daily", priority: "0.8" },
  SOLUTION_LIST: { changefreq: "weekly", priority: "0.8" },
  SOLUTION_CATEGORY: { changefreq: "weekly", priority: "0.8" },
  PRODUCT_LIST: { changefreq: "weekly", priority: "0.8" },
  CASE_LIST: { changefreq: "weekly", priority: "0.8" },
  ABOUT: { changefreq: "monthly", priority: "0.7" },
  CONTACT: { changefreq: "monthly", priority: "0.7" },
  SOLUTION_DETAIL: { changefreq: "monthly", priority: "0.6" },
  PRODUCT_DETAIL: { changefreq: "monthly", priority: "0.6" },
  CASE_DETAIL: { changefreq: "monthly", priority: "0.6" },
  NEWS_DETAIL: { changefreq: "monthly", priority: "0.6" },
};

/** sitemaps.org 0.9 允许的 `<changefreq>` 取值集合，供校验使用 */
export const VALID_CHANGEFREQ: readonly ChangeFreq[] = [
  "always",
  "hourly",
  "daily",
  "weekly",
  "monthly",
  "yearly",
  "never",
];
